import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Circle, Info, Plus, Square, X } from 'lucide-react';
import { toast } from 'sonner';
import { HttpError } from '@vagusx/clients';
import { LABEL_CATEGORY, validateLabelChange, type LabelDefinition } from '@vagusx/shared';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { queryKeys } from '@/lib/query-keys';
import { useLabels } from '@/hooks/use-labels';
import { useUpdateAttachmentLabels } from '@/hooks/use-emails';
import { LabelBadge } from './label-badge';

interface AttachmentLabelsBarProps {
  attachmentId: string;
  labels: string[];
  classificationConfidence?: number | null;
  classificationMethod?: string | null;
  classificationReason?: string | null;
  disabled?: boolean;
}

interface LabelRow {
  id: string;
  name: string;
  displayName: string;
  color?: string | null;
  description?: string | null;
  labelCategory: string;
  sortOrder: number;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Chip({
  label,
  displayName,
  onRemove,
  disabled,
  pending,
}: {
  label: React.ReactNode;
  displayName: string;
  onRemove: () => void;
  disabled: boolean;
  pending: boolean;
}): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-0.5">
      {label}
      {!disabled && (
        <button
          type="button"
          className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={onRemove}
          disabled={pending}
          aria-label={`Remove ${displayName}`}
        >
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </span>
  );
}

function PickerRow({
  label,
  icon: Icon,
  onSelect,
}: {
  label: LabelRow;
  icon: ComponentType<{ className?: string }>;
  onSelect: () => void;
}): React.ReactElement {
  return (
    <CommandItem
      value={`${label.displayName} ${label.description ?? ''}`}
      onSelect={onSelect}
      className="items-start gap-2 py-2"
    >
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="flex min-w-0 flex-col items-start gap-0.5">
        <LabelBadge label={label} size="sm" />
        {label.description && (
          <span className="text-xs leading-snug text-muted-foreground">{label.description}</span>
        )}
      </div>
    </CommandItem>
  );
}

export function formatClassificationMethod(method: string): string {
  switch (method) {
    case 'llm':
    case 'vision':
      return 'VagusX AI';
    case 'fallback':
      return 'Fallback';
    default:
      return method;
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Labels bar for the extraction right panel.
 *
 * One "Labels" section. Chips are removable via ×.
 *
 * The picker lists every enabled label NOT currently on the attachment, grouped:
 *   - Document Type (circle icon) — at most one allowed. To swap, × the
 *     current document_type chip first, then pick a new one.
 *   - Tags (square icon) — additive; workflow_tag + procedural labels go here.
 *     Users can add any label (including procedural ones like voicemail).
 *
 * Shared validateLabelChange runs client-side as a pre-flight check and is
 * the authoritative server-side guard.
 */
export function AttachmentLabelsBar({
  attachmentId,
  labels,
  classificationConfidence,
  classificationMethod,
  classificationReason,
  disabled = false,
}: AttachmentLabelsBarProps): React.ReactElement {
  const queryClient = useQueryClient();
  const { data: allLabels, isLoading } = useLabels();
  const updateLabels = useUpdateAttachmentLabels();
  const [pickerOpen, setPickerOpen] = useState(false);

  // Local state for snappy UI; on error we refetch rather than revert to a
  // possibly-stale client snapshot.
  const [localLabels, setLocalLabels] = useState<string[]>(labels);
  useEffect(() => {
    setLocalLabels(labels);
  }, [labels]);

  const enabled = useMemo(() => allLabels?.filter((l) => l.enabled) ?? [], [allLabels]);

  // Precompute once so sort comparators and chip lookups don't re-scan the array.
  const labelByName = useMemo(() => new Map(enabled.map((l) => [l.name, l])), [enabled]);

  const definitions: LabelDefinition[] = useMemo(
    () =>
      enabled.map((l) => ({
        name: l.name,
        labelCategory: l.labelCategory,
        enabled: l.enabled,
      })),
    [enabled]
  );

  const documentTypeOptions = useMemo(
    () =>
      enabled.filter(
        (l) => l.labelCategory === LABEL_CATEGORY.DOCUMENT_TYPE && !localLabels.includes(l.name)
      ),
    [enabled, localLabels]
  );
  const tagOptions = useMemo(
    () =>
      enabled.filter(
        (l) => l.labelCategory !== LABEL_CATEGORY.DOCUMENT_TYPE && !localLabels.includes(l.name)
      ),
    [enabled, localLabels]
  );

  const chipOrder = useMemo(() => {
    return [...localLabels].sort((a, b) => {
      const la = labelByName.get(a);
      const lb = labelByName.get(b);
      const ca = la?.labelCategory ?? 'z';
      const cb = lb?.labelCategory ?? 'z';
      if (ca !== cb) {
        if (ca === LABEL_CATEGORY.DOCUMENT_TYPE) return -1;
        if (cb === LABEL_CATEGORY.DOCUMENT_TYPE) return 1;
      }
      return (la?.sortOrder ?? 999) - (lb?.sortOrder ?? 999);
    });
  }, [localLabels, labelByName]);

  const commit = (next: string[]): void => {
    const errors = validateLabelChange(next, definitions);
    if (errors.length > 0) {
      toast.error(errors[0]!.message);
      return;
    }
    setLocalLabels(next);
    updateLabels.mutate(
      { attachmentId, data: { labels: next } },
      {
        onError: (err) => {
          queryClient.invalidateQueries({ queryKey: queryKeys.emails.all });
          const message =
            err instanceof HttpError ? err.message : 'Could not update labels. Try again.';
          toast.error(message);
        },
      }
    );
  };

  const addLabel = (slug: string): void => {
    commit([...localLabels, slug]);
    setPickerOpen(false);
  };

  const remove = (slug: string): void => {
    commit(localLabels.filter((l) => l !== slug));
  };

  // Skip chip rendering while labels are loading — otherwise every chip hits
  // the "unknown slug" fallback path and renders as bordered gray text.
  const showChipsRow = !isLoading;

  return (
    <div className="flex min-h-[36px] items-center gap-2 border-b bg-muted/30 px-3 py-1.5">
      <span className="shrink-0 text-[11px] font-medium text-muted-foreground">Labels</span>

      <div className="flex flex-1 flex-wrap items-center gap-1">
        {!showChipsRow && <span className="text-[11px] text-muted-foreground">Loading…</span>}

        {showChipsRow &&
          chipOrder.map((slug) => {
            const known = labelByName.get(slug);
            const displayName = known?.displayName ?? slug;
            const badge = known ? (
              <LabelBadge label={known} size="sm" />
            ) : (
              <span className="rounded border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                {slug}
              </span>
            );
            return (
              <Chip
                key={slug}
                label={badge}
                displayName={displayName}
                onRemove={() => remove(slug)}
                disabled={disabled}
                pending={updateLabels.isPending}
              />
            );
          })}

        {showChipsRow && !disabled && (documentTypeOptions.length > 0 || tagOptions.length > 0) && (
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1 px-2 text-[11px] text-muted-foreground hover:text-foreground"
                disabled={updateLabels.isPending}
              >
                <Plus className="h-3 w-3" />
                Add Label
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <Command
                filter={(value, search) =>
                  value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
                }
              >
                <CommandInput placeholder="Search labels..." />
                <CommandList className="max-h-96">
                  <CommandEmpty>No labels found.</CommandEmpty>

                  {documentTypeOptions.length > 0 && (
                    <CommandGroup heading="Document Type — select one">
                      {documentTypeOptions.map((label) => (
                        <PickerRow
                          key={label.id}
                          label={label}
                          icon={Circle}
                          onSelect={() => addLabel(label.name)}
                        />
                      ))}
                    </CommandGroup>
                  )}

                  {documentTypeOptions.length > 0 && tagOptions.length > 0 && <CommandSeparator />}

                  {tagOptions.length > 0 && (
                    <CommandGroup heading="Tags — select any">
                      {tagOptions.map((label) => (
                        <PickerRow
                          key={label.id}
                          label={label}
                          icon={Square}
                          onSelect={() => addLabel(label.name)}
                        />
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>

      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Show classification metadata"
            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3 text-xs" align="end">
          <p className="mb-2 font-medium text-muted-foreground">Original AI Classification</p>
          <dl className="space-y-1.5">
            {classificationMethod && (
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Method</dt>
                <dd className="font-medium">{formatClassificationMethod(classificationMethod)}</dd>
              </div>
            )}
            {classificationConfidence != null && (
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Confidence</dt>
                <dd className="font-medium">{Math.round(classificationConfidence * 100)}%</dd>
              </div>
            )}
            {classificationReason && (
              <div className="pt-1">
                <dt className="mb-0.5 text-muted-foreground">Reasoning</dt>
                <dd className="text-foreground">{classificationReason}</dd>
              </div>
            )}
            {!classificationMethod && !classificationConfidence && !classificationReason && (
              <p className="text-muted-foreground">No classification metadata recorded.</p>
            )}
            <p className="pt-2 text-[10px] text-muted-foreground">
              These are the AI&apos;s original call at processing time. User edits do not update
              this record.
            </p>
          </dl>
        </PopoverContent>
      </Popover>
    </div>
  );
}
